import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CustomSelect } from "@/components/ui/custom-select";
import { GameViplayConfigFields } from "@/components/games/GameViplayConfigFields";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormInput } from "@/components/ui/form-input";
import { cn } from "@/lib/utils";
import { gameService } from "@/services/game.service";
import { Icon } from "@/components/ui/icon"

interface GameViplaySyncModalProps {
  gameId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSynced?: () => void;
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: `${String(i).padStart(2, "0")}:00`,
}));

export function GameViplaySyncModal({ gameId, open, onOpenChange, onSynced }: GameViplaySyncModalProps) {
  const [arenaId, setArenaId] = useState("");
  const [quadraIds, setQuadraIds] = useState<string[]>([]);
  const [date, setDate] = useState("");
  const [hourStart, setHourStart] = useState("0");
  const [hourEnd, setHourEnd] = useState("23");
  const [fullDay, setFullDay] = useState(false);
  const [expandHourBuffer, setExpandHourBuffer] = useState(true);
  const [configApplied, setConfigApplied] = useState(false);

  const configQuery = useQuery({
    queryKey: ["viplaySyncConfig", gameId],
    queryFn: () => gameService.getViplaySyncConfig(gameId),
    enabled: open && !!gameId,
    retry: false,
  });

  useEffect(() => {
    if (!open) {
      setConfigApplied(false);
      return;
    }
    if (configApplied || configQuery.isLoading) return;

    const cfg = configQuery.data;
    if (cfg) {
      setDate(cfg.suggestedDate);
      setHourStart(String(cfg.suggestedHourStart));
      setHourEnd(String(cfg.suggestedHourEnd));
      setArenaId(cfg.viplayArenaId ?? "");
      setQuadraIds(cfg.viplayQuadraIds ?? []);
    } else {
      setDate(new Date().toISOString().slice(0, 10));
      setHourStart("0");
      setHourEnd("23");
      setArenaId("");
      setQuadraIds([]);
    }
    setFullDay(false);
    setExpandHourBuffer(true);
    setConfigApplied(true);
  }, [open, configQuery.data, configQuery.isLoading, configApplied]);

  const effectiveSearchHours = useMemo(() => {
    if (fullDay) return { start: 0, end: 23 };
    const lo = Math.min(Number(hourStart), Number(hourEnd));
    const hi = Math.max(Number(hourStart), Number(hourEnd));
    if (expandHourBuffer) {
      return { start: Math.max(0, lo - 1), end: Math.min(23, hi + 1) };
    }
    return { start: lo, end: hi };
  }, [fullDay, hourStart, hourEnd, expandHourBuffer]);

  const formatHourRange = (start: number, end: number) =>
    `${String(start).padStart(2, "0")}h–${String(end).padStart(2, "0")}h`;

  const syncMutation = useMutation({
    mutationFn: () =>
      gameService.syncViplayGallery(gameId, {
        arenaId,
        quadraIds: quadraIds.length ? quadraIds : undefined,
        date,
        hourStart: Number(hourStart),
        hourEnd: Number(hourEnd),
        fullDay,
        expandHourBuffer: fullDay ? false : expandHourBuffer,
      }),
    onSuccess: (result) => {
      const rangeNote =
        result.searchHourStart != null && result.searchHourEnd != null
          ? ` · busca ${formatHourRange(result.searchHourStart, result.searchHourEnd)}`
          : "";
      const skippedNote = result.skipped > 0 ? ` · ${result.skipped} já estavam na galeria` : "";
      const unwatchableNote =
        result.unwatchable > 0 ? ` · ${result.unwatchable} sem link de vídeo` : "";
      const foundNote =
        result.totalFetched > 0 ? ` (${result.totalFetched} encontrados${rangeNote})` : "";

      if (result.added > 0) {
        toast.success(
          result.added === 1
            ? `1 lance adicionado à galeria${foundNote}${skippedNote}${unwatchableNote}`
            : `${result.added} lances adicionados à galeria${foundNote}${skippedNote}${unwatchableNote}`,
        );
      } else if (result.skipped > 0) {
        toast.info(
          `Todos os ${result.skipped} lance(s) encontrados já estavam na galeria${foundNote}`,
        );
      } else if (result.totalFetched === 0) {
        toast.warning(
          "Nenhum lance nessa data/horário — confira ano, arena, quadras e marque 'Dia inteiro'",
        );
      } else if (result.matchedHour === 0) {
        toast.warning("Nenhum lance disponível para importar — tente ampliar as horas ou marcar dia inteiro");
      } else {
        toast.warning("Nenhum lance novo para importar");
      }
      onSynced?.();
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Não foi possível buscar os lances");
    },
  });

  const canSubmit = !!arenaId && !!date && !syncMutation.isPending && !configQuery.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent layout="flex" size="lg" className="p-0">
        <DialogHeader className="border-b border-border p-6 pb-4 pr-14">
          <DialogTitle className="flex items-center gap-2">
            <Icon name="videocam" />
            Importar lances
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {configQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando configuração...</p>
          ) : (
            <>
              <GameViplayConfigFields
                arenaId={arenaId}
                quadraIds={quadraIds}
                viplayAutoSync={false}
                onArenaIdChange={setArenaId}
                onQuadraIdsChange={setQuadraIds}
                onViplayAutoSyncChange={() => undefined}
                showAutoSync={false}
                className="border-0 bg-transparent p-0"
              />

              <FormInput
                label="Data"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />

              <div className="flex flex-col gap-2">
                <label className="flex cursor-pointer items-center gap-2.5 text-sm">
                  <input
                    type="checkbox"
                    checked={fullDay}
                    onChange={(e) => setFullDay(e.target.checked)}
                    className="h-4 w-4 rounded border-border accent-primary"
                  />
                  <span>Importar dia inteiro (00h–23h)</span>
                </label>
                {!fullDay && (
                  <label className="flex cursor-pointer items-center gap-2.5 text-sm">
                    <input
                      type="checkbox"
                      checked={expandHourBuffer}
                      onChange={(e) => setExpandHourBuffer(e.target.checked)}
                      className="h-4 w-4 rounded border-border accent-primary"
                    />
                    <span>Ampliar 1h antes e depois</span>
                  </label>
                )}
              </div>

              <div className={cn("grid grid-cols-2 gap-3", fullDay && "pointer-events-none opacity-50")}>
                <CustomSelect
                  label="Hora inicial"
                  value={hourStart}
                  onChange={setHourStart}
                  options={HOUR_OPTIONS}
                  size="sm"
                  disabled={fullDay}
                />
                <CustomSelect
                  label="Hora final"
                  value={hourEnd}
                  onChange={setHourEnd}
                  options={HOUR_OPTIONS}
                  size="sm"
                  disabled={fullDay}
                />
              </div>

              {!fullDay && (
                <p className="text-xs text-muted-foreground">
                  Busca lances entre {formatHourRange(effectiveSearchHours.start, effectiveSearchHours.end)}.
                  {hourStart === hourEnd && !expandHourBuffer
                    ? " Hora inicial e final iguais, sem ampliar: só esse horário entra."
                    : null}{" "}
                  Para pegar todos do dia, marque &quot;Importar dia inteiro&quot;.
                </p>
              )}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={syncMutation.isPending}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => syncMutation.mutate()} disabled={!canSubmit} className="gap-1.5">
            {syncMutation.isPending ? (
              <>
                <Icon name="progress_activity" />
                Importando...
              </>
            ) : (
              <>
                <Icon name="download" />
                Importar lances
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
